import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Image,
  VStack,
  HStack,
  Menu,
  MenuButton,
  Button,
  MenuList,
  MenuItem,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Fade,
  Divider,
  Flex,
  Spinner,
} from "@chakra-ui/react";
import { FaChevronDown } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { languages } from '../data/languages';
import FullscreenImageViewer from "../components/FullscreenImageViewer";
import { getMenuTheme, getRichTextSx } from "../utils/theme";

const hasImage = (url) => typeof url === "string" && url.trim() !== "";

const SingleMenu = () => {
  const { id } = useParams(); // matches /:language/:id
  const [selectedLang, setSelectedLang] = useState(languages[1]);
  const [menu, setMenu] = useState({});
  const [fullscreenImage, setFullscreenImage] = useState(null);

  // Track loading state of section images by section id
  const [imageLoading, setImageLoading] = useState({});

  // Refs to each section's AccordionItem, keyed by section id, so the
  // newly expanded section can be smooth-scrolled into view.
  const sectionRefs = useRef({});

  const openFullscreen = (imageUrl) => setFullscreenImage(imageUrl);
  const closeFullscreen = () => setFullscreenImage(null);

  // Caches fetched menus per id+language so switching back to an already
  // loaded language doesn't hit the backend again.
  const menuCacheRef = useRef(new Map());

  useEffect(() => {
    const cacheKey = `${id}/${selectedLang.code}`;
    const cached = menuCacheRef.current.get(cacheKey);
    if (cached) {
      setMenu(cached);
      return;
    }

    fetch(`https://masterpiece-prod-csftcwdvg2ecfmfr.westeurope-01.azurewebsites.net/api/menus/${id}/${selectedLang.code}`)
      .then((res) => res.json())
      .then((data) => {
        menuCacheRef.current.set(cacheKey, data);
        setMenu(data);
      })
      .catch((err) => {
        console.error('Failed to fetch menu:', err);
      });
  }, [id, selectedLang]);

  // Called when image finishes loading or errors (hide spinner)
  const handleImageLoad = (id) => {
    setImageLoading((prev) => ({ ...prev, [id]: false }));
  };

  const theme = getMenuTheme(menu.colorScheme);

  return (
    <Box bg={theme.appBg} minH="100vh" p={6}>
      <Box maxW={{ base: "100%", md: "2xl" }} mx="auto">
      <Flex justify="space-between" align="center" mb={6}>
        {hasImage(menu.locationLogo) && (
          <Image src={menu.locationLogo} alt="Logo" height="3em" />
        )}

        {/* Language Picker */}
        <Menu>
          <MenuButton
            as={Button}
            rightIcon={<FaChevronDown />}
            variant="outline"
            size="sm"
            px={3}
            py={1.5}
            borderRadius="md"
          >
            <HStack spacing={2}>
              <Image
                src={selectedLang.flag}
                alt={selectedLang.label}
                boxSize="20px"
                borderRadius="full"
                userSelect="none"
              />
              <Text fontWeight="medium" userSelect="none">{selectedLang.label}</Text>
            </HStack>
          </MenuButton>
          <MenuList>
            {Array.isArray(languages) && languages.map((lang) => (
              <MenuItem
                key={lang.code}
                onClick={() => setSelectedLang(languages.find(l => l.code === lang.code))}
                _hover={{ bg: 'transparent' }}   // disables hover background
                _focus={{ bg: 'transparent' }}   // disables focus background (optional)
                _active={{ bg: 'transparent' }}  // disables click background (optional)
              >
                <HStack spacing={3}>
                  <Image
                    src={lang.flag}
                    alt={lang.label}
                    boxSize="20px"
                    borderRadius="full"
                    userSelect="none"
                  />
                  <Text>{lang.label}</Text>
                </HStack>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Flex>

      {/* Header */}
      <HStack mb={8} alignItems="center" justifyContent="center">
        <Heading size="lg" userSelect="none" color={theme.textPrimary}>
          {menu.name}
        </Heading>
      </HStack>

      {/* No sections available */}
      {Array.isArray(menu.sections) && menu.sections.length === 0 && (
        <Text textAlign="center" fontSize="lg" color={theme.textSecondary} userSelect="none">
          Nema dostupnih sekcija.
        </Text>
      )}

      {/* Sections Accordion */}
      <Accordion
        allowToggle
        onChange={(expandedIndex) => {
          if (typeof expandedIndex !== "number" || expandedIndex === -1) return;
          const sectionId = menu.sections?.[expandedIndex]?.id;
          const el = sectionRefs.current[sectionId];
          if (el) {
            setTimeout(() => {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 300);
          }
        }}
      >
        {Array.isArray(menu.sections) && menu.sections.map((section) => (
          <Fade in key={section.id}>
            <AccordionItem
              ref={(el) => (sectionRefs.current[section.id] = el)}
              border="none"
              borderRadius="lg"
              overflow="hidden"
              boxShadow="lg"
              bg={theme.cardBg}
              mb={6}
            >
              <AccordionButton _expanded={{ bg: theme.primaryLight }} px={4} py={5}>
                <HStack
                  flex="1"
                  spacing={4}
                  justify="space-between"
                  align="center"
                >
                  <HStack spacing={4} align={section.price !== 0.00 ? "start" : "center"}>
                    {hasImage(section.image) && (
                      <Box position="relative" boxSize="64px" flexShrink={0} borderRadius="md" overflow="hidden">
                        {/* Spinner while loading */}
                        {imageLoading[section.id] && (
                          <Box
                            position="absolute"
                            top="50%"
                            left="50%"
                            transform="translate(-50%, -50%)"
                            zIndex={1}
                            pointerEvents="none"
                          >
                            <Spinner size="sm" color={theme.primary} />
                          </Box>
                        )}

                        <Image
                          src={section.image}
                          alt={section.name}
                          boxSize="64px"
                          borderRadius="md"
                          objectFit="cover"
                          onLoad={() => handleImageLoad(section.id)}
                          onError={() => handleImageLoad(section.id)}
                          visibility={imageLoading[section.id] ? "hidden" : "visible"}  // Hide image until loaded
                        />
                      </Box>
                    )}

                    <Box textAlign="left">
                      <Heading size="md" userSelect="none" mb={1} color={theme.textPrimary}>
                        {section.name}
                      </Heading>
                      {section.price !== 0.00 && (
                        <Text
                          fontWeight="bold"
                          fontSize="md"
                          color={theme.primary}
                          whiteSpace="nowrap"
                          userSelect="none"
                          mr={3}
                        >
                          {Number(section.price).toFixed(2)} {section.currency}
                        </Text>
                      )}
                    </Box>
                  </HStack>

                  <AccordionIcon />
                </HStack>
              </AccordionButton>

              <AccordionPanel pb={4} px={6}>
                <VStack
                  align="start"
                  spacing={4}
                  w="100%"
                  divider={<Divider borderColor={theme.border} />}
                >
                  {Array.isArray(section.items) && section.items.map((item) => {
                    const isImageValid = item.image && item.image.trim() !== "";

                    return (
                      <Box
                        key={item.id}
                        w="100%"
                        onClick={() => isImageValid && openFullscreen(item.image)}
                        cursor={isImageValid ? "pointer" : "default"}
                        _hover={isImageValid ? { bg: theme.cardHover } : {}}
                        transition="background 0.2s"
                      >
                        <HStack justify="space-between" align="start" spacing={3}>
                          <Text fontWeight="semibold" fontSize="md" userSelect="none" color={theme.textPrimary} flex="1">
                            {item.name}
                          </Text>

                          {section.price === 0.00 && item.price && (
                            <Text fontWeight="bold" fontSize="md" color={theme.textPrimary} userSelect="none" whiteSpace="nowrap" flexShrink={0}>
                              {Number(item.price).toFixed(2)} {item.currency}
                            </Text>
                          )}
                        </HStack>

                        {item.description?.trim() && (
                          <Text fontSize="sm" color={theme.textMuted} mt={1} userSelect="none">
                            {item.description}
                          </Text>
                        )}

                        {item.ingredients?.trim() && (
                          <Text fontSize="sm" color={theme.textSecondary} mt={1} fontStyle="italic" userSelect="none">
                            {item.ingredients}
                          </Text>
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          </Fade>
        ))}
      </Accordion>

      {/* Menu Description */}
      {menu.description?.trim() && (
        <Box mt={10} px={[4, 6]}>
          <Text fontStyle="italic" fontSize="md" color={theme.textSecondary} textAlign="center">
            {menu.description !== undefined ? menu.description : ""}
          </Text>
        </Box>
      )}

      {/* Special Notes */}
      {menu.specialNotes != null && menu.specialNotes.trim() !== "" && (
        <Box
          mt={8}
          p={6}
          bg={theme.cardBg}
          borderRadius="lg"
          boxShadow="md"
          sx={getRichTextSx(theme)}
          dangerouslySetInnerHTML={{ __html: menu.specialNotes }}
        />
      )}

      {/* Footer */}
      <Box mt={16} pt={8} borderTop="1px solid" borderColor={theme.border}>
        <VStack spacing={4}>
          <Text fontSize="sm" color={theme.textSecondary} userSelect="none">
            {menu.locationName !== undefined ? menu.locationName : ""}
          </Text>
          <Text fontSize="xs" color={theme.textMuted} textAlign="center" px={4} userSelect="none">
            It is prohibited serving or allowing consumption of alcoholic beverages by persons under 18 years.
          </Text>
        </VStack>
      </Box>

      <FullscreenImageViewer imageUrl={fullscreenImage} onClose={closeFullscreen} />
      </Box>
    </Box>
  );
};

export default SingleMenu;